import 'app/modules/remote-control-ng/context/rcContext';
import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginPageModule } from './modules/login/login.module';
import {
  AppSettingsService,
  AuthenticationService,
  AuthorizationService,
  ConsoleLoggerService,
  LoggerService,
  MessageTransportService,
  TicketContextService,
  XDeviceService,
  XSi18nService,
} from '@nx-fe/services';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { HttpClientModule } from '@angular/common/http';
import { CoreModule } from 'sspcore/core.module';
import { TicketModule } from './modules/ticket/ticket.module';
import { AlertService } from '@nx-fe/alert';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { storeFreeze } from 'ngrx-store-freeze';
import { environment } from '@nx-fe/environment';
import { rootReducer } from 'sspshared/features/app-state';
import { SspAuthorizationService } from 'sspservices/ssp-authorization.service';
import { SspRestApiService } from 'sspservices/sspRestApiService';
import { EnrollModule } from './modules/enroll/enroll.module';
import { BasePageModule } from './modules/core/basepage.module';
import { ExpiredSessionModule } from '@nx-fe/expired-session';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedComponentsModule } from './shared/shared-components.module';
import { DeviceContextService } from './services/device-context.service';
import { AppConfigStateService } from 'shared/features/ng-redux/app/appconfig-state.service';
import { DidRestClientService } from 'core/services';

export const metaReducers = environment.production ? [] : [storeFreeze];

@NgModule({
  declarations: [AppComponent],
  imports: [
    TranslateModule.forRoot(),
    StoreModule.forRoot(rootReducer, { metaReducers }),
    CoreModule,
    SharedComponentsModule,
    BasePageModule,
    ExpiredSessionModule,
    BrowserModule,
    HttpClientModule,
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    LoginPageModule,
    TicketModule,
    EnrollModule,
    BrowserAnimationsModule,
    EffectsModule.forRoot([]),
  ],
  providers: [
    DidRestClientService,
    SspRestApiService,
    AuthenticationService,
    SspAuthorizationService,
    DeviceContextService,
    XDeviceService,
    AlertService,
    AppSettingsService,
    AppConfigStateService,
    MessageTransportService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [TicketContextService, AuthenticationService, AuthorizationService],
      multi: true,
    },
    { provide: LoggerService, useClass: ConsoleLoggerService },
    { provide: 'MENU_URLS', useFactory: getMenuUrls },
    { provide: 'LOCALSTORAGE', useFactory: getLocalStorage },
    { provide: 'SESSIONSTORAGE', useFactory: getSessionStorage },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private i18n: XSi18nService) {
    console.log(i18n);
  }
}

export function initializeApp(
  ticketContextService: TicketContextService,
  authenticationService: AuthenticationService,
  authorizationService: AuthorizationService
) {
  return () =>
    new Promise<void>(resolve => {
      retrieveAuthentication(authenticationService)()
        .then(() => retrieveUserClaims(authorizationService)())
        .then(() => {
          initializeTicketContextService(ticketContextService)();
          initializeAuthenticationHandlers(authenticationService)();
        })
        .finally(() => resolve());
    });
}

export function getLocalStorage() {
  return typeof window !== 'undefined' ? window.localStorage : null;
}

export function getSessionStorage() {
  return typeof window !== 'undefined' ? window.sessionStorage : null;
}

export function retrieveUserClaims(authorizationService: AuthorizationService) {
  return () => authorizationService.loadClaims().then(() => console.log('User claims loaded'));
}

export function getMenuUrls() {
  return {};
}

export function retrieveAuthentication(authenticationService: AuthenticationService) {
  return () => {
    return authenticationService.isAuthenticated().catch(_ => false);
  };
}

export function initializeTicketContextService(ticketContextService: TicketContextService) {
  return (): Promise<void> =>
    new Promise<void>(resolve => {
      ticketContextService.initializeService();
      resolve();
    });
}

export function initializeAuthenticationHandlers(authService: AuthenticationService) {
  return (): Promise<void> => {
    return new Promise<void>(resolve => {
      authService.startRefreshSessionTimer();
      resolve();
    });
  };
}
